import React, { FC, useEffect, useState, useMemo } from 'react';
import { Box, Button, Popover } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RangeSliderExtended, SelectWithSearch } from 'ui';
import { FiltersData, FundsInitialFiltersData, RangeFilter } from '../../types';
import { valueParser, rangeSliderValuesGenerator, minMaxValueInitialValues, updateFilters } from '../../utils';

type DatatableFundsFiltersProps = {
  filterFieldsInitialData: FundsInitialFiltersData;
  prevFiltersValues: FiltersData;
  handleSubmit: (value: FiltersData) => void;
};

export const DatatableFundsFilters: FC<DatatableFundsFiltersProps> = ({
  filterFieldsInitialData,
  handleSubmit,
  prevFiltersValues,
}) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  const id = open ? 'datatable-filters-popover' : undefined;

  const [dataToSubmit, setDataToSubmit] = useState<FiltersData>({});

  useEffect(() => {
    setDataToSubmit(prevFiltersValues ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prevFiltersValues]);

  const { peergroup, rating, ratingPoints } = filterFieldsInitialData;

  const parsed = {
    fundPeerGroup: valueParser(peergroup.length ? peergroup : []),
    // fundRank: valueParser(rank.length ? rank : []),
    fundRating: valueParser(rating.length ? rating : []),
    fundRatingPoints: useMemo(() => rangeSliderValuesGenerator(ratingPoints), [ratingPoints]), 
    // fundReturnRate: useMemo(() => rangeSliderValuesGenerator(returnRate), [returnRate]),
    // fundRiskRate: useMemo(() => rangeSliderValuesGenerator(riskRate), [riskRate]),
  }

  const values = {
    fundPeerGroup: parsed.fundPeerGroup,
    // fundRank: minMaxValueInitialValues(parsed.fundRank), 
    fundRating: minMaxValueInitialValues(parsed.fundRating),
    fundRatingPoints: minMaxValueInitialValues(parsed.fundRatingPoints), 
    // fundReturnRate: minMaxValueInitialValues(parsed.fundReturnRate), 
    // fundRiskRate: minMaxValueInitialValues(parsed.fundRiskRate), 
  };

  const handleSelectChange = (name: keyof FiltersData) => (value: any) =>
    setDataToSubmit(updateFilters(dataToSubmit, name, value));

  const handleRangeSliderChange = (name: string) => (newValues: RangeFilter) => {
    setDataToSubmit({
      ...dataToSubmit,
      [name]: newValues,
    });
  };

  const checkFiltersAvailability = () => {
    return true;
  };

  return (
    <>
      {checkFiltersAvailability() && (
        <Button size="small" onClick={handleClick}>
          <FilterList fontSize="large" />
          <p>{t('FILTERS')}</p>
        </Button>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2} width="500px">
          {!!parsed.fundPeerGroup.length && (
            <SelectWithSearch
              label={t('PORTFOLIO.FUNDS_PEERGROUP')}
              options={parsed.fundPeerGroup}
              value={dataToSubmit.peergroupName ?? null}
              onChange={handleSelectChange('peergroupName')}
            />
          )}
          {/* {!!parsed.fundRank.length && (
            <RangeSliderExtended
              label={t('PORTFOLIO.FUNDS_RANK')}
              options={parsed.fundRank}
              values={dataToSubmit.rank ?? values.fundRank}
              onChange={handleRangeSliderChange('rank')}
            />
          )} */}
          {!!parsed.fundRating.length && (
            <RangeSliderExtended
              label={t('PORTFOLIO.FUNDS_RATING')}
              options={parsed.fundRating}
              values={dataToSubmit.rating ?? values.fundRating}
              onChange={handleRangeSliderChange('rating')}
            />
          )}
          {!!parsed.fundRatingPoints.length && (
            <RangeSliderExtended
              label={t('PORTFOLIO.FUNDS_RATING_POINTS')}
              options={parsed.fundRatingPoints}
              values={dataToSubmit.ratingPoints ?? values.fundRatingPoints}
              onChange={handleRangeSliderChange('ratingPoints')}
            />
          )}
          {/* {!!parsed.fundReturnRate.length && (
            <RangeSliderExtended
              label={t('PORTFOLIO.FUNDS_RETURN')}
              options={parsed.fundReturnRate}
              values={dataToSubmit.returnValue ?? values.fundReturnRate}
              onChange={handleRangeSliderChange('returnValue')}
            />
          )}
          {!!parsed.fundRiskRate.length && (
            <RangeSliderExtended
              label={t('PORTFOLIO.FUNDS_RISK')}
              options={parsed.fundRiskRate}
              values={dataToSubmit.risk ?? values.fundRiskRate}
              onChange={handleRangeSliderChange('risk')}
            />
          )} */}

          <Box mt={2} display="flex" justifyContent="space-between">
            <Button type="submit" onClick={() => setDataToSubmit({})}>
              {t('RESET')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => {
                handleSubmit(dataToSubmit);
                handleClose();
              }}
            >
              {t('APPLY_FILTERS')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
